import { MedicationLabelData } from "@/lib/medication-label";
import { getTimezone } from "@/lib/timezone";

import { PatientSource } from "@/types/enums/patient-source";
import {
  MeasurementCreate,
  MeasurementSummary,
  MeasurementValueSummary,
} from "@/types/measurement";
import {
  DoseManagementStatus,
  MedicationCreate,
  MedicationSummary,
  MedicationUpdate,
} from "@/types/medication";
import { Page } from "@/types/pagination";
import {
  DynamicHealthField,
  DynamicHealthFieldValue,
  MergeSummary,
  PatientChart,
  PatientCreate,
  PatientSummary,
  PatientTable,
  PatientTableRequest,
  PatientUpdate,
} from "@/types/patient";
import { PaymentMethod } from "@/types/payment";
import { ReviewSummary } from "@/types/review";

import { kyInstance, requestData } from "@/api/api-config";

export interface PatientMedicationState {
  patientId: string;
  medications: MedicationSummary[];
}

export async function createPatient(
  patient: PatientCreate | Partial<PatientCreate>,
): Promise<PatientSummary | null> {
  return await requestData<PatientSummary>(
    "user/patient/create",
    "post",
    {
      json: {
        ...patient,
        type: "PATIENT",
        patientSource: patient.patientSource ?? PatientSource.MANUALLY_CREATED,
      },
    },
    false,
  );
}

export async function archivePatients(
  patientIds: string[],
  isArchived: boolean,
): Promise<boolean | null> {
  return await requestData<boolean>(
    "user/patient/archive",
    "post",
    {
      json: { patientIds, isArchived },
    },
    false,
  );
}

export async function changeBlacklistStatus(
  patientId: string,
  status: boolean,
): Promise<boolean | null> {
  return await requestData<boolean>(
    `user/patient/blacklist/${patientId}?status=${status}`,
    "put",
    undefined,
    false,
  );
}

export async function getBlacklist(
  controller: AbortController | null = null,
): Promise<PatientSummary[] | null> {
  return await requestData<PatientSummary[]>(
    "user/patient/blacklist",
    "get",
    {
      signal: controller?.signal,
    },
    false,
  );
}

export async function searchPatient(
  searchTerm: string,
  controllerOrSignal: AbortController | AbortSignal | null = null,
  n?: number,
): Promise<PatientSummary[] | null> {
  const signal =
    controllerOrSignal instanceof AbortController
      ? controllerOrSignal.signal
      : controllerOrSignal;

  return await requestData<PatientSummary[]>(
    "user/patient/search",
    "get",
    {
      searchParams: n != null ? { searchTerm, n } : { searchTerm },
      signal,
    },
    false,
  );
}

export async function getPatient(
  patientId: string,
): Promise<PatientChart | null> {
  const tz = getTimezone();
  return await requestData<PatientChart>(
    `user/patient/${patientId}`,
    "get",
    {
      searchParams: { tz },
    },
    false,
  );
}

export async function updatePatient(
  userId: string,
  user: PatientUpdate,
): Promise<PatientSummary | null> {
  return await requestData<PatientSummary>(
    `user/patient/${userId}`,
    "put",
    {
      json: user,
    },
    false,
  );
}

export async function updatePatientPreferredProvider(
  patientId: string,
  preferredProviderId: string | null,
): Promise<boolean | null> {
  return await requestData<boolean>(
    `user/patient/${patientId}/preferred-provider`,
    "put",
    {
      json: { preferred_provider_id: preferredProviderId },
    },
    false,
  );
}

export async function updatePatientDynamicFields(
  patientId: string,
  dynamicFields: Record<string, (string | DynamicHealthFieldValue)[]>,
): Promise<{ healthData: Record<string, any> } | null> {
  return await requestData<{ healthData: Record<string, DynamicHealthField> }>(
    `user/patient/${patientId}/dynamic-fields`,
    "put",
    {
      json: dynamicFields,
    },
    false,
  );
}

export async function createMedication(
  medication: MedicationCreate,
): Promise<MedicationSummary | null> {
  return await requestData<MedicationSummary>(
    "user/patient/doses",
    "post",
    {
      json: medication,
    },
    false,
  );
}

export async function deleteMedication(
  medicationId: string,
): Promise<PatientMedicationState | null> {
  return await requestData<PatientMedicationState>(
    `user/patient/doses/${medicationId}`,
    "delete",
    undefined,
    false,
  );
}

export async function findDuplicates(
  fields: string[],
): Promise<MergeSummary[] | null> {
  return await requestData<MergeSummary[]>(
    "user/patient/find-duplicates",
    "get",
    {
      searchParams: { fields: fields.join(",") },
    },
    false,
  );
}

export async function findDuplicatesOfPatient(
  patientId: string,
  fields?: string[],
): Promise<PatientSummary[] | null> {
  const searchParams: Record<string, any> = {};
  if (fields && fields.length > 0) {
    searchParams.fields = fields.join(",");
  }

  return await requestData<PatientSummary[]>(
    `user/patient/${patientId}/duplicates`,
    "get",
    {
      searchParams:
        Object.keys(searchParams).length > 0 ? searchParams : undefined,
    },
    false,
  );
}

export async function listPatients(
  patientIds: string[],
): Promise<PatientSummary[] | null> {
  return await requestData<PatientSummary[]>(
    "user/patient/list",
    "get",
    {
      searchParams: { patientIds },
    },
    false,
  );
}

export async function getLeadSources(): Promise<string[] | null> {
  return await requestData<string[]>(
    "user/patient/lead-sources",
    "get",
    undefined,
    false,
  );
}

export async function mergeDuplicates(
  merges: MergeSummary[],
): Promise<PatientSummary | null> {
  const onlyIdMerges = merges.map((merge) => ({
    keep_id: merge.keep.id,
    merge_ids: merge.merge.map((p) => p.id),
    void_other_charges: merge.voidOtherCharges,
  }));

  return await requestData<PatientSummary>(
    "user/patient/merge-duplicates",
    "post",
    {
      json: onlyIdMerges,
    },
    false,
  );
}

export async function fetchPatientTable(
  params: PatientTableRequest,
  controller: AbortController | null = null,
): Promise<Page<PatientTable> | null> {
  const tz = getTimezone();
  return await requestData<Page<PatientTable>>(
    "user/patient/table",
    "get",
    {
      searchParams: { ...params, tz },
      signal: controller?.signal,
    },
    false,
  );
}

export async function checkIfExternalIdIsUnique(
  externalId: string,
): Promise<boolean | null> {
  return await requestData<boolean>(
    `user/patient/external-id-exists/${externalId}`,
    "get",
    undefined,
    false,
  );
}

export async function deletePaymentMethod(
  paymentMethodId: string,
  patientId: string,
): Promise<boolean | null> {
  return await requestData<boolean>(
    `user/patient/payment-methods/${paymentMethodId}`,
    "delete",
    {
      searchParams: { patientId },
    },
    false,
  );
}

/**
 * Vault a card the browser has just captured.
 *
 * Takes the processor's id, not the card object: a Stripe merchant has nothing
 * else to send, and the server reads the card back through the processor seam
 * either way.
 */
export async function createPaymentMethod(
  patientId: string,
  paymentMethodId: string,
): Promise<PaymentMethod | null> {
  return await requestData<PaymentMethod>(
    "user/patient/payment-methods/create",
    "post",
    {
      json: { paymentMethodId },
      searchParams: { patientId },
    },
    false,
  );
}

export async function fetchPatientIds(
  filters: PatientTableRequest,
  controller?: AbortController,
): Promise<string[]> {
  const tz = getTimezone();
  const searchParams = { ...filters, tz };
  const result = await requestData<string[]>(
    "user/patient/ids",
    "get",
    {
      searchParams,
      signal: controller?.signal,
    },
    false,
  );
  return result ?? [];
}

type PatientMeasurementPostResponse =
  | MeasurementSummary
  | MeasurementValueSummary[];

export function normalizePatientMeasurementResponse(
  measurement: MeasurementCreate,
  response: PatientMeasurementPostResponse | null,
): MeasurementSummary | null {
  if (!response) return null;
  if (!Array.isArray(response)) return response;

  const primaryValue =
    response.find((value) => !value.isDerived) ?? response[0];
  if (!primaryValue) return null;

  return {
    id: primaryValue.id,
    patientId: measurement.patientId,
    measurementDate:
      primaryValue.measurementDate ?? measurement.measurementDate,
    createdDate: primaryValue.createdDate ?? new Date().toISOString(),
    creatorId: undefined,
    height: measurement.height,
    weight: measurement.weight,
    systolicBp: measurement.systolicBp,
    diastolicBp: measurement.diastolicBp,
    pulse: measurement.pulse,
    temperature: measurement.temperature,
    waistCircumference: measurement.waistCircumference,
    noteId: measurement.noteId,
    eventId: measurement.eventId,
  };
}

export async function addMeasurement(
  measurement: MeasurementCreate,
): Promise<MeasurementSummary | null> {
  const response = await requestData<PatientMeasurementPostResponse>(
    `user/patient/measurement`,
    "post",
    {
      json: measurement,
    },
    false,
  );
  return normalizePatientMeasurementResponse(measurement, response);
}

export async function updateMedication(
  medicationId: string,
  medication: MedicationUpdate,
): Promise<PatientMedicationState | null> {
  return await requestData<PatientMedicationState>(
    `user/patient/doses/${medicationId}`,
    "put",
    { json: medication },
    false,
  );
}

export async function updateDoseManagementStatus(
  doseId: string,
  managementStatus: DoseManagementStatus | null,
): Promise<PatientMedicationState | null> {
  return await requestData<PatientMedicationState>(
    `user/patient/doses/${doseId}/management-status`,
    "patch",
    { json: { managementStatus } },
    false,
  );
}

export async function signOffMedication(
  medicationId: string,
): Promise<MedicationSummary | null> {
  return await requestData<MedicationSummary>(
    `user/patient/doses/${medicationId}/sign-off`,
    "post",
    undefined,
    false,
  );
}

export async function saveDoseLabel(
  doseId: string,
  labelData: MedicationLabelData,
  action: "save" | "print",
): Promise<MedicationSummary | null> {
  const response = await requestData<MedicationSummary>(
    `user/patient/doses/${doseId}/label`,
    "post",
    { json: { labelData, action } },
    false,
  );
  return response;
}

export async function updatePatientProfilePicture(
  patientId: string,
  file?: File | null,
): Promise<string | null> {
  if (file) {
    // Upload new profile picture
    const formData = new FormData();
    formData.append("file", file);

    return await requestData<string>(
      `user/patient/${patientId}/profile-picture`,
      "post",
      {
        body: formData,
      },
      false,
    );
  } else {
    // Remove profile picture (send request with no body)
    return await requestData<string>(
      `user/patient/${patientId}/profile-picture`,
      "post",
      undefined,
      false,
    );
  }
}

export async function getPatientReviews(
  patientId: string,
): Promise<ReviewSummary[] | null> {
  return await requestData<ReviewSummary[]>(
    `user/patient/${patientId}/reviews`,
    "get",
    undefined,
    false,
  );
}

export interface PatientEditLogEntry {
  id: string;
  action: string;
  fieldName: string | null;
  oldValue: string | null;
  newValue: string | null;
  source: string;
  sourceRefId: string | null;
  sourceRefName: string | null;
  batchId: string | null;
  createdById: string | null;
  createdByName: string | null;
  createdDate: string;
}

export async function getPatientEditHistory(
  patientId: string,
): Promise<PatientEditLogEntry[] | null> {
  return await requestData<PatientEditLogEntry[]>(
    `user/patient/${patientId}/edit-history`,
    "get",
    undefined,
    false,
  );
}

export async function fetchPatientExportZip(
  patientId: string,
  signal?: AbortSignal,
): Promise<{ blob: Blob; filename: string }> {
  // Zip building scales with note/document count; raise the default 20s ky
  // timeout so large records don't error out client-side.
  const response = await kyInstance.get(`user/patient/${patientId}/export`, {
    timeout: 120000,
    signal,
  });
  const blob = await response.blob();
  const disposition = response.headers.get("content-disposition") ?? "";
  const match = disposition.match(/filename="?([^";]+)"?/i);
  const filename = match?.[1] ?? `patient-record-${patientId}.zip`;
  return { blob, filename };
}

export interface NoteAttachmentForExport {
  id: string;
  name: string;
  gcsPath: string;
  annotations: Record<string, unknown>[] | null;
  parentAttachmentId: string | null;
  versionIndex: number;
  createdDate: string;
  // Directory of the linked note's parent group in the export zip (e.g.
  // "notes/2026-05-01-cheek-augment-not_abc"). Null when the attachment
  // isn't linked to any note — those stay in the top-level
  // "note-attachments/" directory.
  noteParentDir: string | null;
  // Relative directory grouping every revision of this attachment so
  // annotation history files together (e.g.
  // "2026-05-02-cheek-before-nta_xyz"). The frontend prepends either
  // "note-attachments/" or "{noteParentDir}/attachments/" depending on
  // whether the chain is linked to a note.
  attachmentParentDir: string;
}

export async function fetchPatientAttachmentsForExport(
  patientId: string,
  signal?: AbortSignal,
): Promise<NoteAttachmentForExport[]> {
  const result = await requestData<NoteAttachmentForExport[]>(
    `user/patient/${patientId}/note-attachments/export-list`,
    "get",
    { signal },
    false,
  );
  return result ?? [];
}

export async function fetchPatientAttachmentRawBundle(
  patientId: string,
  signal?: AbortSignal,
): Promise<Blob> {
  // One request returns a zip of every attachment's raw bytes (keyed by
  // attachment id) so the export stays O(1) round-trips regardless of how
  // many attachments the patient has. Routed through the API to avoid the
  // GCS CORS restriction on browser fetch of signed URLs.
  const response = await kyInstance.get(
    `user/patient/${patientId}/note-attachments/raw-bundle`,
    { timeout: 180000, signal },
  );
  return await response.blob();
}
